import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { BedDouble, Activity, Pill, Clock } from 'lucide-react';
import { dashboardApi } from '@/lib/api';
import type { NurseDashboardData, MedRoute } from './types';
import {
  MetricCard, MetricsGrid, SectionCard, GenericBadge,
  DashboardShell, ScrollTable, Th, Td, LoadingState, ErrorState,
} from './_shared';

const ROUTE_STYLE: Record<MedRoute, string> = {
  IV:      'bg-red-50 text-red-700 border-red-200',
  ORAL:    'bg-gray-100 text-gray-600 border-gray-200',
  IM:      'bg-orange/15 text-orange-dark border-orange/30',
  SC:      'bg-violet-50 text-violet-700 border-violet-200',
  TOPICAL: 'bg-blue-50 text-blue-700 border-blue-200',
};

function RouteTag({ route }: { route: MedRoute }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold border', ROUTE_STYLE[route])}>
      {route}
    </span>
  );
}

// ── Component ───────────────────────────────────────────────────────────────
export default function NurseDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<NurseDashboardData>({
    queryKey:        ['dashboard-nurse'],
    queryFn:         () => dashboardApi.nurse().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  const occupancyPct = Math.round((data.wardOccupancy / data.totalBeds) * 100);
  const occupancyColor = occupancyPct >= 90 ? '#ef4444' : occupancyPct >= 75 ? '#f59e0b' : '#10b981';

  const pending   = data.medications.filter(m => !m.administered);
  const completed = data.medications.filter(m =>  m.administered);

  return (
    <DashboardShell
      title="Ward Nurse Dashboard"
      subtitle="Medication schedule & ward occupancy"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      <MetricsGrid>
        <MetricCard
          icon={BedDouble}
          label="Ward Occupancy"
          value={`${data.wardOccupancy}/${data.totalBeds}`}
          accent="bg-orange/15"
          sub={
            <div className="flex items-center gap-2 mt-0.5">
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${occupancyPct}%`, background: occupancyColor }}/>
              </div>
              <span className="text-2xs font-semibold" style={{ color: occupancyColor }}>{occupancyPct}%</span>
            </div>
          }
        />
        <MetricCard icon={Activity} label="Pending Vitals"        value={data.pendingVitals}      accent="bg-amber-50" alert={data.pendingVitals > 0}/>
        <MetricCard icon={Pill}     label="Medications Next 2h"   value={data.medicationsNext2h}  accent="bg-blue-50"/>
        <MetricCard icon={Clock}    label="Administered Today"     value={completed.length}        accent="bg-emerald-50"/>
      </MetricsGrid>

      <SectionCard title={`Medication Schedule · Due Soon (${pending.length})`}>
        <ScrollTable>
          <thead>
            <tr>
              <Th>Bed</Th><Th>Patient</Th><Th>Drug</Th>
              <Th>Dose</Th><Th>Route</Th><Th>Scheduled</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {data.medications.map(m => (
              <tr key={m.id} className={clsx(
                'hover:bg-gray-50 transition',
                m.administered && 'opacity-50',
              )}>
                <Td>
                  <span className="font-mono text-xs font-semibold text-navy bg-navy/5 px-2 py-0.5 rounded">
                    {m.bedNumber}
                  </span>
                </Td>
                <Td><span className="font-medium">{m.patientName}</span></Td>
                <Td>{m.drugName}</Td>
                <Td muted>{m.dose}</Td>
                <Td><RouteTag route={m.route}/></Td>
                <Td muted>{m.scheduledTime}</Td>
                <Td>
                  {m.administered
                    ? <GenericBadge label="Administered" variant="success"/>
                    : <GenericBadge label="Pending" variant="warning"/>
                  }
                </Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </SectionCard>

    </DashboardShell>
  );
}
